import { defineComponent as _defineComponent } from 'vue'
import AdUnit from '@/components/AdUnit.vue';
import EventBackdrop from '@/features/sports/components/event/EventBackdrop.vue';
import EventBuybox from '@/features/sports/components/event/EventBuybox.vue';
import EventOverview from '@/features/sports/components/event/EventOverview.vue';
import EventSpinningText from '@/features/sports/components/event/EventSpinningText.vue';
import SingleStepEventDetails from '@/features/sports/components/event/SingleStepEventDetails.vue';
import SingleStepEventLineup from '@/features/sports/components/event/SingleStepEventLineup.vue';
import SingleStepEventEncounterList from '@/features/sports/components/global/SingleStepEventEncounterList.vue';
import SportsLayout from '@/features/sports/components/global/SportsLayout.vue';
import HeadToHead from '@/features/sports/components/event/HeadToHead.vue';
import { shiftHours } from '@/helpers/date-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { trackClickOutSports } from '@/helpers/offer-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { getSportPageContext, SingleStepEventContext } from '@/features/sports/helpers/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { Maybe, Platform, SingleStepSportEvent, SportEventStatus, SportOffer, SportType } from '@/@types/graphql-types';
import {
	GetSportEventV2Document,
	GetSportEventV2Query,
	GetSportEventV2QueryVariables,
} from '@/features/sports/graphql/queries/GetSportEventV2.query';
import { isMultiStepSport } from '@/features/sports/types/enums';
import { toastError } from '@/helpers/toast/toast-helper';
import { SportEventV2Url, SportPageType } from '@/features/sports/types/interfaces';
import { useQuery } from '@/helpers/composables/useApollo';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useRoutingStore, useLanguageStore, useMetaStore, useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useSportEvents } from '@/features/sports/composables/useSportEvents';


import { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'SingleStepEvent',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const route = useRoute();
const { country, language, webLocale, locales } = useLanguageStore();
const { setJsonLd } = useMetaStore();
const { t, tc } = useI18n();
const { timeZone } = useUserStore();
const { activeDomain } = useRoutingStore();

const queryVariables = computed<GetSportEventV2QueryVariables>(() => {
	// cut out query parameters that make gql queries hiccups
	const fullPath = route.fullPath.toString().split('?')[0];
	return {
		language: language.value,
		country: country.value,
		platform: Platform.Web,
		fullPath,
		site: activeDomain.value,
	};
});

const event = computed(() => {
	return eventData.value?.node ?? {};
});

const context = ref<SingleStepEventContext>({} as SingleStepEventContext);

const { sportEvents } = useSportEvents({ context });

const eventTitle = computed(() => {
	const { key, params } = context.value.getEventTitle(event.value, webLocale.value);
	return t(key, params) as string;
});

const competitionName = computed(() => {
	return event.value?.competition?.content.name;
});

const schemaData = computed(() => {
	const seasonName = event.value.content?.season?.name ?? '';
	const description = `${eventTitle.value} ${competitionName.value} ${seasonName}`;
	const sportName = t(context.value.sportName);
	let eventStatus = '';
	switch (event.value.content?.status) {
		case SportEventStatus.Postponed:
			eventStatus = 'EventPostponed';
			break;
		case SportEventStatus.Cancelled:
			eventStatus = 'EventCancelled';
			break;
		default:
			eventStatus = 'EventScheduled';
	}

	const endDate = shiftHours(event.value.content?.startAt, 2)?.toISOString();

	const prices =
		event.value?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ?? [];
	const schemaDataObject = {
		'@context': 'https://schema.org',
		'@type': 'BroadcastEvent',
		name: description,
		description,
		isLiveBroadcast: (event.value?.offers ?? []).length > 0,
		startDate: event.value.content?.startAt,
		endDate,
		organizer: {
			'@type': 'Organization',
			name: competitionName.value,
		},
		eventAttendanceMode: 'OnlineEventAttendanceMode',
		broadcastOfEvent: {
			'@type': 'SportsEvent',
			sport: sportName,
			organizer: {
				'@type': 'Organization',
				name: competitionName.value,
			},
			eventStatus,
			name: description,
			description,
			awayTeam: {
				'@type': 'SportsTeam',
				name: event.value?.competitors?.awayTeam?.competitors.map(c => c.content.name).find(Boolean) ?? '',
				sport: sportName,
			},
			homeTeam: {
				'@type': 'SportsTeam',
				name: event.value?.competitors?.homeTeam?.competitors.map(c => c.content.name).find(Boolean) ?? '',
				sport: sportName,
			},
			startDate: event.value.content?.startAt,
			endDate,
			location: {
				'@type': 'SportsActivityLocation',
				name: event.value.content?.venue?.name,
				address: {
					addressLocality: event.value.content?.venue?.city,
					addressCountry: event.value.content?.venue?.country,
				},
			},
			offers: event.value?.offers
				? {
						'@type': 'AggregateOffer',
						highPrice: Math.max(...prices) ?? undefined,
						lowPrice: Math.min(...prices) ?? undefined,
						priceCurrency: event.value.offers.map((offer: any) => offer.currency).find(Boolean),
						offerCount: event.value.offers.length,
						offers: event.value.offers.map((offer: any) => ({
							'@type': 'Offer',
							name: `${description} ${offer.presentationType} ${offer.monetizationType}`,
							price: offer.retailPrice,
							priceCurrency: offer.currency,
							url: offer.standardWebURL,
						})),
				  }
				: null,
			audience: {
				'@type': 'Audience',
				name: 'Sports Fans',
			},
		},
	};

	return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
});

const enabled = computed(() => {
	return route.name != null && route.name.includes('event');
});

const { result, onResult, onError, loading } = useQuery<GetSportEventV2Query, GetSportEventV2QueryVariables>(
	GetSportEventV2Document,
	queryVariables,
	{
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
		enabled,
	}
);

onResult(() => {
	setJsonLd(schemaData.value);
});

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Single Step Event Data]:',
		{ error: error, where: 'Component: SingleStepEventPage' },
		'error'
	);
	process.client ? showErrorToast() : null;
});

const eventData = computed<SportEventV2Url<SingleStepSportEvent>>(() => {
	return result.value?.urlV2 as SportEventV2Url<SingleStepSportEvent>;
});

function showErrorToast() {
	toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
}

const head = computed(() => {
	return {};
});

expose({ head });

watch(
	() => route.meta?.sport,
	(newMeta: SportType | undefined) => {
		if (newMeta && newMeta !== context.value.sportType && !isMultiStepSport(newMeta)) {
			context.value = getSportPageContext({
				sport: route.meta!.sport as SportType,
				pageType: SportPageType.SportEvent,
			});
		}
	},
	{ immediate: true }
);

const isNBA = computed(() => {
	return event.value.competition?.id === 'scn-bkb-8331232';
});

const isNFL = computed(() => event.value.competition?.id === 'scn-afb-7440571');
const isMLB = computed(() => event.value.competition?.id === 'scn-bsb-11678542');
const isNHL = computed(() => event.value.competition?.id === 'scn-ice-17698463');

const stage = computed(() => {
	const now = new Date().getTime();

	if (isNBA.value) {
		if (now <= new Date('2024-04-14').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	if (
		isNFL.value &&
		![
			'AMERICAN_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
			'AMERICAN_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
			'AMERICAN_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
			'NATIONAL_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
			'NATIONAL_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
			'NATIONAL_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
		].includes(event.value.content.competitionStage?.name as string)
	) {
		if (now <= new Date('2024-01-12').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	if (isMLB.value) {
		if (now <= new Date('2023-10-01').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	if (isNHL.value) {
		if (now <= new Date('2024-04-18').setUTCHours(23, 59, 59, 999)) return tc('SPORTS_WEBAPP_REGULAR_SEASON');
	}

	return context.value?.getStageHeading(event.value.content.competitionStage, t);
});

// in case the user switches the country of the same event, we need to listen to changes to locale changes as well
const pageIdentity = computed(() => {
	return [event.value.id, eventData.value?.locale].filter(Boolean).join('__');
});

const sportContext = computed(() => {
	return SnowplowSportContext.fromSportEventV2(context.value.sportType, context.value.pageType, event.value);
});

watch(
	pageIdentity,
	(newIdentity, oldIdentity) => {
		if (newIdentity && newIdentity !== oldIdentity) {
			// note: all sport pages are excluded from automatic page tracking
			TrackingHelper.trackPage(route.path, [sportContext.value]);
		}
	},
	{ immediate: true }
);

const competitionId = computed(() => {
	return event.value.competition?.objectId;
});

const eventStatus = computed(() => {
	return event.value?.content?.status;
});

// is the title detail page in loading mode for:
// - requests
const isDataLoading = computed(() => {
	return loading.value && event.value != null;
});

const hasRelevantOffers = computed(() => {
	return (event.value?.offers ?? []).length > 0;
});

const pastEvents = computed(() => {
	return event.value?.content.pastEvents ?? [];
});

const displayHeadToHead = computed(() => {
	return pastEvents.value.length > 0 && context.value.pageType === SportPageType.SportEvent;
});

const eventStartAt = computed(() => {
	return event.value.content?.startAt == null ? null : new Date(event.value.content.startAt);
});

const kickoffDate = computed(() => {
	if (eventStartAt.value == null) return undefined;
	return formatDate(eventStartAt.value, locales.value, webLocale.value, timeZone.value, language.value);
});

const kickoffTime = computed(() => {
	if (eventStartAt.value == null) return undefined;
	return formatTime(eventStartAt.value, locales.value, webLocale.value, timeZone.value, language.value);
});

const spinningTextProps = computed(() => {
	return {
		event: event.value,
		eventTitle: eventTitle.value,
		status: eventStatus.value,
		date: kickoffDate.value,
		time: kickoffTime.value,
	};
});

const isNotificationActive = computed(() => {
	return !!event.value.mysportlistEntry?.createdAt;
});

function handleClickout(
	offerLocation: 'backdrop' | 'buybox' | 'watchnow',
	{ offer, index }: { offer?: Maybe<SportOffer>; index: number | undefined }
) {
	if (offer == null) return;
	trackClickOutSports(offer, index, 0, offer.sportObjectType, offerLocation, [sportContext.value]);
}

async function openCountrySwitcherModal() {
	const { default: CountrySwitcherModal } = await import('@/features/buybox/components/CountrySwitcherModal.vue');
	ModalHelper.openModal(CountrySwitcherModal, { closable: true, additionalContexts: [sportContext.value] });
	TrackingHelper.trackEvent('userinteraction', { action: 'country_selector_clicked' }, [sportContext.value]);
}

return { __sfc: true,route, country, language, webLocale, locales, setJsonLd, t, tc, timeZone, activeDomain, queryVariables, event, context, sportEvents, eventTitle, competitionName, schemaData, enabled, result, onResult, onError, loading, eventData, showErrorToast, head, isNBA, isNFL, isMLB, isNHL, stage, pageIdentity, sportContext, competitionId, eventStatus, isDataLoading, hasRelevantOffers, pastEvents, displayHeadToHead, eventStartAt, kickoffDate, kickoffTime, spinningTextProps, isNotificationActive, handleClickout, openCountrySwitcherModal, AdUnit, EventBackdrop, EventBuybox, EventOverview, EventSpinningText, SingleStepEventDetails, SingleStepEventLineup, SingleStepEventEncounterList, SportsLayout, HeadToHead }
}

})